import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Sparkles, BookOpen, Languages, Hash, FileText, CheckCircle2, Clock, AlertCircle } from 'lucide-react';
import { enrichUserLibraryBooks, getUserLibraryEnrichmentStats } from '@/utils/enrichUserLibraryBooks';
import { Alert, AlertDescription } from '@/components/ui/alert';

export function UserLibraryEnrichmentPanel() {
  const [stats, setStats] = useState<any>(null);
  const [isEnriching, setIsEnriching] = useState(false);
  const [isLoadingStats, setIsLoadingStats] = useState(true);

  const loadStats = async () => {
    setIsLoadingStats(true);
    try {
      const data = await getUserLibraryEnrichmentStats();
      setStats(data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    } finally {
      setIsLoadingStats(false);
    }
  };

  useEffect(() => {
    loadStats();
  }, []);

  const handleEnrich = async () => {
    setIsEnriching(true);
    try {
      await enrichUserLibraryBooks();
      // Reload stats after enrichment
      await loadStats();
    } catch (error) {
      console.error('Enrichment failed:', error);
    } finally {
      setIsEnriching(false);
    }
  };

  const completionRate = stats ? Math.round((stats.complete / stats.total) * 100) : 0;
  const needsEnrichment = stats ? stats.pending + (stats.total - stats.complete) : 0;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              User Library Enrichment
            </CardTitle>
            <CardDescription>
              Enrich books currently in user libraries with ISBNdb data
            </CardDescription>
          </div>
          <Button
            onClick={handleEnrich}
            disabled={isEnriching || isLoadingStats}
            size="lg"
          >
            {isEnriching ? (
              <>
                <Clock className="w-4 h-4 mr-2 animate-spin" />
                Enriching...
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 mr-2" />
                Enrich Library Books
              </>
            )}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Priority Alert */}
        {stats && needsEnrichment > 0 && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <strong>{needsEnrichment} books</strong> in user libraries need enrichment.
              These are high-priority as they directly impact user experience.
            </AlertDescription>
          </Alert>
        )}

        {/* Stats Grid */}
        {isLoadingStats ? (
          <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-4">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="h-24 bg-muted animate-pulse rounded-lg" />
            ))}
          </div>
        ) : stats ? (
          <>
            <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <BookOpen className="w-8 h-8 text-muted-foreground" />
                    <div className="text-right">
                      <p className="text-2xl font-bold">{stats.total}</p>
                      <p className="text-xs text-muted-foreground">Total Books</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <CheckCircle2 className="w-8 h-8 text-green-500" />
                    <div className="text-right">
                      <p className="text-2xl font-bold">{stats.complete}</p>
                      <p className="text-xs text-muted-foreground">Complete</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <Clock className="w-8 h-8 text-orange-500" />
                    <div className="text-right">
                      <p className="text-2xl font-bold">{stats.pending}</p>
                      <p className="text-xs text-muted-foreground">Pending</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div className="text-3xl">📊</div>
                    <div className="text-right">
                      <p className="text-2xl font-bold">{completionRate}%</p>
                      <p className="text-xs text-muted-foreground">Complete Rate</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <FileText className="w-8 h-8 text-blue-500" />
                    <div className="text-right">
                      <p className="text-2xl font-bold">{stats.withSynopsis}</p>
                      <p className="text-xs text-muted-foreground">With Synopsis</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div className="text-3xl">📚</div>
                    <div className="text-right">
                      <p className="text-2xl font-bold">{stats.withSeries}</p>
                      <p className="text-xs text-muted-foreground">With Series</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <Hash className="w-8 h-8 text-purple-500" />
                    <div className="text-right">
                      <p className="text-2xl font-bold">{stats.withPageCount}</p>
                      <p className="text-xs text-muted-foreground">With Pages</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <Languages className="w-8 h-8 text-amber-500" />
                    <div className="text-right">
                      <p className="text-2xl font-bold">{stats.nonEnglish}</p>
                      <p className="text-xs text-muted-foreground">Non-English</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Data Quality Breakdown */}
            <div>
              <h3 className="font-semibold mb-3">Data Quality Breakdown</h3>
              <div className="space-y-2">
                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <span className="text-sm">Books with ISBNs</span>
                  <Badge variant="outline">{stats.withISBN} / {stats.total}</Badge>
                </div>
                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <span className="text-sm">Books with Synopses</span>
                  <Badge variant="outline">{stats.withSynopsis} / {stats.total}</Badge>
                </div>
                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <span className="text-sm">Books with Series Data</span>
                  <Badge variant="outline">{stats.withSeries} / {stats.total}</Badge>
                </div>
                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <span className="text-sm">Books with Page Counts</span>
                  <Badge variant="outline">{stats.withPageCount} / {stats.total}</Badge>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            Failed to load statistics
          </div>
        )}

        {/* Info Box */}
        <Alert>
          <Sparkles className="h-4 w-4" />
          <AlertDescription>
            <strong>ISBNdb Priority Enrichment:</strong> This will use ISBNdb as the primary source
            for books with ISBNs, ensuring high-quality English synopses, accurate page counts, and
            proper series information. Books without ISBNs will use Google Books as a fallback.
          </AlertDescription>
        </Alert>
      </CardContent>
    </Card>
  );
}
